import type Store from "@ember-data/store";
import type ApplicationInstance from "@ember/application/instance";
import { getOwner } from "@ember/owner";
import dayjs from "dayjs";
import { Changeset, EmberChangeset } from "ember-changeset";
import lookupValidator from "ember-changeset-validations";
import { validateDate } from "ember-changeset-validations/validators";
import type { BufferedChangeset, ValidatorMap } from "ember-changeset/types";
import type IntlService from "ember-intl/services/intl";
import type Patient from "parog-web/models/patient";
import { sortBy } from "parog-web/utils/sort";
import type TypedEmberChangeset from "parog-web/utils/typed-ember-changeset";
import type DpMeasuredJournalEvent from "./dp-measured-journal-event";
import type DpSessionDeclaredJournalEvent from "./dp-session-declared-journal-event";
import type HemodialysisMeasuredJournalEvent from "./hemodialysis-measured-journal-event";
import type HemodialysisSessionDeclaredJournalEvent from "./hemodialysis-session-declared-journal-event";

export interface Measurement<
  T extends
    | HemodialysisMeasuredJournalEvent
    | DpMeasuredJournalEvent
    | EmberChangeset
> {
  start: T[];
  middle: T[];
  end: T[];
}

type DialysisSession =
  | HemodialysisSessionDeclaredJournalEvent
  | DpSessionDeclaredJournalEvent;

export type DialysisMeasured =
  | HemodialysisMeasuredJournalEvent
  | DpMeasuredJournalEvent;

class OccurredAtTime extends EmberChangeset {
  get occurredAtTime(): string | undefined {
    // eslint-disable-next-line ember/no-get, ember/classic-decorator-no-classic-methods
    const occurredAt = this.get("occurredAt");

    if (!occurredAt) {
      return undefined;
    }

    return new Intl.DateTimeFormat("fr-FR", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false
    }).format(occurredAt);
  }

  set occurredAtTime(value: string) {
    const [hours, minutes] = value.split(":").map(Number);

    const occurredAt = new Date();
    occurredAt.setHours(hours, minutes, 0, 0);

    (this as unknown as EmberChangeset).set("occurredAt", occurredAt);
  }
}

/**
 * Abstract base class to generate a model for Hemodialysis or DP session measurements.
 *
 * The goal of this class is to generate a model that will be used by the
 *  Hemodialysis::SessionMeasurement and Dp::SessionMeasurement view.
 *
 * Subclasses are responsible for:
 *  - Defining `measurementValidations()` for measurement-specific rules.
 *  - Implementing `validate()` for persistence logic.
 *  - Implementing `completedArrayWithMissingMeasurements()` to normalize measurement arrays.
 *  - Indicate `sessionKey` to save the correct session type
 */
export default abstract class DialysisSessionDeclaredModelView<
  TMeasured extends DialysisMeasured
> {
  measurementChangesets: Measurement<TypedEmberChangeset<TMeasured>>;
  measurements: Measurement<TMeasured>;
  sessionChangeset: EmberChangeset;

  /**
   * Every mesurement changeset flattened
   */
  get allChangesets(): EmberChangeset[] {
    return Object.values(this.measurementChangesets).flat();
  }

  /**
   * Every changeset that have been used
   */
  get allUsedChangesets(): BufferedChangeset[] {
    return this.allChangesets.filter(changeset => changeset.isDirty);
  }

  get intl(): IntlService {
    return (getOwner(this.store) as ApplicationInstance).lookup("service:intl");
  }

  get isDirty(): boolean {
    return (
      this.sessionChangeset.isDirty ||
      this.allChangesets.some(changeset => changeset.isDirty)
    );
  }

  protected normalizedChangesets?: EmberChangeset[];
  protected abstract sessionKey: string;

  private get sessionValidations(): { occurredAt: unknown[] } {
    return {
      occurredAt: [
        validateDate({
          message:
            "Cette donnée ne peut être déclarée plus de 50 ans en avance. Veuillez modifier la date",
          onOrBefore: dayjs().add(50, "years").toDate()
        })
      ]
    };
  }

  constructor(
    protected store: Store,
    protected session: DialysisSession,
    protected patient: Patient
  ) {
    this.sessionChangeset = Changeset(
      this.session,
      lookupValidator(this.sessionValidations),
      this.sessionValidations
    );

    this.measurements = this.createMissingMeasurements();

    const measurementValidations = this.measurementValidations();

    this.measurementChangesets = {
      start: [
        Changeset(
          this.measurements.start[0],
          lookupValidator(measurementValidations),
          measurementValidations
        ) as TypedEmberChangeset<TMeasured>
      ],
      middle: this.measurements.middle.map(
        m =>
          Changeset(
            m,
            lookupValidator(measurementValidations),
            measurementValidations,
            { changeset: OccurredAtTime }
          ) as unknown as TypedEmberChangeset<TMeasured>
      ),
      end: [
        Changeset(
          this.measurements.end[0],
          lookupValidator(measurementValidations),
          measurementValidations,
          { changeset: OccurredAtTime }
        ) as unknown as TypedEmberChangeset<TMeasured>
      ]
    };
  }

  /**
   * A session should be composed of one measure for start and end and at least one for middle.
   * This methods will return new HemodialysisMeasuredJournalEvent/DpSessionDeclaredJournalEvent
   *  for each missing measurements The method will format the measurement in a Measurements interface
   */
  createMissingMeasurements(): Measurement<TMeasured> {
    return {
      start: this.completedArrayWithMissingMeasurements({
        measures: this.session.startMeasurement
          ? [this.session.startMeasurement]
          : [],
        moment: "start"
      }),

      middle: this.completedArrayWithMissingMeasurements({
        measures: this.session.middleMeasurements,
        moment: "middle"
      }),

      end: this.completedArrayWithMissingMeasurements({
        measures: this.session.endMeasurement
          ? [this.session.endMeasurement]
          : [],
        moment: "end"
      })
    };
  }

  /**
   * Save the session and set it's occurretAt
   */
  async ensureSession(): Promise<DialysisSession> {
    if (!this.session || this.session.isNew) {
      this.session.set("isDraft", true);
      this.session.set("patient", this.patient);

      // set the session occurredAt with the start measure occurredAt
      const startOccurredAt = this.allChangesets
        .find(cs => cs.get("moment") === "start")
        ?.get("occurredAt");

      this.session.set("occurredAt", startOccurredAt);

      await this.session.save();
    }
    return this.session;
  }

  /**
   * set it's occurretAt
   */
  measureOccurredAtChangesets(changesets: EmberChangeset[]): EmberChangeset[] {
    // set the session occurredAt date with the start measure occurredAt
    const startOccurredAt = this.allChangesets
      .find(cs => cs.get("moment") === "start")
      ?.get("occurredAt");

    if (!startOccurredAt) return changesets;

    for (const changeset of changesets) {
      if (
        !changeset.isDirty ||
        changeset.moment === "start" ||
        !changeset.occurredAtTime
      )
        continue;

      const startOccurredAtDate = new Date(startOccurredAt);
      const occurredAtDate = new Date(changeset.occurredAt as Date);

      if (!this.isSameDay(startOccurredAt, occurredAtDate)) {
        const occurredAtTime = changeset.occurredAtTime as string;

        const newOccurredAt = this.applyTimeToDate(
          startOccurredAtDate,
          occurredAtTime
        );

        changeset.set("occurredAt", newOccurredAt);
      }
    }

    return changesets;
  }

  async save(): Promise<void> {
    const changesets = this.getChangesetsForSave();

    const orderedChangesets = changesets.sort(sortBy("occurredAt"));
    const session = await this.ensureSession();

    for (const changeset of orderedChangesets) {
      if (changeset.isDirty) {
        changeset.set(this.sessionKey, session);
        await this.saveChangeset(changeset);
        changeset.rollback();

        if (changeset.moment === "end") {
          this.session.set("isDraft", false);
          await this.session.save();
        }
      }
    }
  }

  saveChangeset(
    changeset: EmberChangeset
  ): Promise<HemodialysisMeasuredJournalEvent> {
    changeset.set("patient", this.patient);
    changeset.execute();
    return (changeset.data as HemodialysisMeasuredJournalEvent).save();
  }

  validateOccurredAtAgainstPrevious(changesets: EmberChangeset[]) {
    const timeSetChangeset = changesets.filter(cs => cs.isDirty || !cs.isNew);

    timeSetChangeset.forEach((current, index) => {
      if (index === 0 || current.moment == "before") return;

      if (!current.isDirty) return;
      current.removeError("occurredAtChronology");

      const previous = timeSetChangeset[index - 1];

      const previousDate = previous.get("occurredAt") as Date | undefined;
      const currentDate = current.get("occurredAt") as Date | undefined;

      if (!previousDate || !currentDate) return;

      if (currentDate < previousDate) {
        current.addError(
          "occurredAtChronology",
          `La date doit être postérieure à la mesure précédente (${dayjs(previousDate).format("DD/MM/YYYY à HH:mm")})`
        );
      }
    });
  }

  /**
   * Save every changeset
   * The method will not check if the changesets are valid or not, you need to call 'validate' first
   */
  validateOccurredAtTimeAgainstPrevious(changesets: EmberChangeset[]) {
    const timeSetChangeset = changesets.filter(
      cs => cs.occurredAtTime || cs.moment == "start"
    );

    timeSetChangeset.forEach((current, index) => {
      if (index === 0 || current.moment == "start") return;

      if (!current.isDirty) return;

      const previous = timeSetChangeset[index - 1];

      const previousTime = new Intl.DateTimeFormat("fr-FR", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: false
      }).format(previous.get("occurredAt"));

      const time = current.get("occurredAtTime");

      if (time < previousTime) {
        current.addError(
          "occurredAtTime",
          `L'heure doit être postérieure à la mesure précédente (${previousTime})`
        );
      }
    });
  }

  validateOccurredAtTimePrecense(changesets: EmberChangeset[]) {
    changesets
      .filter(cs => !cs.occurredAtTime && cs.moment !== "start")
      .forEach(cs =>
        cs.addError("occurredAtTime", "L'heure doit être remplie")
      );
  }

  /**
   * Return a copy of the given array that add every missing measurements
   */
  protected abstract completedArrayWithMissingMeasurements({
    measures,
    moment
  }: {
    measures: DialysisMeasured[];
    moment: string;
  }): TMeasured[];

  protected getChangesetsForSave() {
    return this.allUsedChangesets;
  }

  protected getOccurredAt(_moment: string): Date | undefined {
    return this.session.occurredAt;
  }

  protected abstract measurementValidations(): ValidatorMap;

  protected abstract validate(): Promise<boolean>;

  private applyTimeToDate(date: Date, time: string): Date {
    const [hours, minutes] = time.split(":").map(Number);
    const d = new Date(date);
    d.setHours(hours, minutes, 0, 0);
    return d;
  }

  private isSameDay(a: Date, b: Date): boolean {
    return (
      a.getFullYear() === b.getFullYear() &&
      a.getMonth() === b.getMonth() &&
      a.getDate() === b.getDate()
    );
  }
}
